import { assert } from '@disy/lib/custom-error';
import { getUrlParam } from '@disy/lib/url-param-util';

import type {
  AddDrillThroughCommandReportPayload,
  AddDrillThroughCommandViewPayload,
  AddDrillThroughCommandWorksheetPayload,
  EditDrillThroughCommandPayload,
} from 'cadenza/api-client/workbook-command-payloads';
import { Command } from 'cadenza/api-client/workbook-commands';
import { postEvent } from 'cadenza/integration/post-message';
import type { Map } from 'cadenza/map/map';
import { getStore } from 'cadenza/store/cadenza-store';
import type { CadenzaLinkSettings } from 'cadenza/ui/link/cadenza-link-types';
import type { PartialContext } from 'cadenza/ui/link/model/partial-context.gen';
import type { DrillThroughDto } from 'cadenza/workbook/drill-through/model/drill-through-dto';
import type { DrillThroughId } from 'cadenza/workbook/drill-through/model/drill-through-id.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { getViewByDataViewId } from 'cadenza/workbook/workbook-core-selectors.state';

/**
 * Adds a drill-through target pointing to the specified view
 *
 * @param payload - the payload for this command
 * @return a promise for the operation's result
 */
export function addViewDrillThrough(payload: Omit<AddDrillThroughCommandViewPayload, 'type'>) {
  return getStore().dispatch(
    executeCommand(
      Command.ADD_DRILLTHROUGH({
        type: 'VIEW',
        ...payload,
      }),
    ),
  );
}

/**
 * Adds a drill-through target pointing to the specified worksheet
 *
 * @param payload - the payload for this command
 * @return A promise for the operation's result
 */
export function addWorksheetDrillThrough(payload: Omit<AddDrillThroughCommandWorksheetPayload, 'type'>) {
  return getStore().dispatch(
    executeCommand(
      Command.ADD_DRILLTHROUGH({
        type: 'WORKSHEET',
        ...payload,
      }),
    ),
  );
}

/**
 * Adds a drill-through target pointing to the specified report
 *
 * @param payload - the payload for this command
 * @return A promise for the operation's result
 */
export function addReportDrillThrough(payload: Omit<AddDrillThroughCommandReportPayload, 'type'>) {
  return getStore().dispatch(
    executeCommand(
      Command.ADD_DRILLTHROUGH({
        type: 'REPORT',
        ...payload,
      }),
    ),
  );
}

/* Edits drill-through target to worksheet. */
export function editWorksheetDrillThrough(changedProperties: EditDrillThroughCommandPayload) {
  assert(changedProperties.id, 'Id is required');
  return getStore().dispatch(executeCommand(Command.EDIT_DRILLTHROUGH(changedProperties)));
}

/**
 * Removes a drill-through target specified by the given ID from the workbook
 *
 * @param drillThroughId - The drill-through target id
 * @return A promise for the operation's result
 */
export function removeDrillThrough(drillThroughId: DrillThroughId) {
  return getStore().dispatch(
    executeCommand(
      Command.REMOVE_DRILLTHROUGH({
        drillThroughId,
      }),
    ),
  );
}

export async function openDynamicDrillThrough(
  dataViewId: DataViewId,
  drillThrough: DrillThroughDto,
  selectionIndex?: number,
  map?: Map,
) {
  if (drillThrough.type === 'POST_MESSAGE') {
    let values = drillThrough.values;
    const view = getViewByDataViewId(getStore().getState(), dataViewId);
    if (map && view.type === 'MAP') {
      const { addGeometriesToSelectionValues, addGeometryToSingleObjectValues } = await import(
        'cadenza/integration/cadenza-api-selection-util'
      );
      values =
        selectionIndex == null ?
          await addGeometriesToSelectionValues(values, map.getView())
        : await addGeometryToSingleObjectValues(values, map.getView(), selectionIndex);
    }
    postEvent('drillThrough', { context: drillThrough.context, values });
    return;
  }

  const { id, targetSettings } = drillThrough;
  let link;
  const commonLinkArguments = {
    drillThroughId: id,
    targetSettings,
    sourceDataViewId: dataViewId,
    selectionIndex,
  };

  // copy of targetSettings because parts are immutable
  const linkTargetSettings = {
    ...targetSettings,
  };
  // if a drill-through with linkTarget = POPUP is already opened in a Popup, then open it in a new tab
  const partialContext = getUrlParam<PartialContext>('partialContext');
  if (
    linkTargetSettings?.linkTarget === 'POPUP' &&
    (partialContext === 'viewPopup' || partialContext === 'worksheetPopup')
  ) {
    linkTargetSettings.linkTarget = 'NEW_WINDOW';
  }

  switch (drillThrough.type) {
    case 'WORKSHEET':
      {
        const { workbookName, worksheetId, repositoryName, showWorkbookToolbar, transferConditionMode } = drillThrough;
        link = await importAndCreateCadenzaLink({
          type: 'worksheet',
          workbookName,
          worksheetId,
          repositoryName,
          transferSelectionAsFilter: true,
          ...commonLinkArguments,
          transferConditionMode,
          showWorkbookToolbar,
          ...linkTargetSettings,
          partialContext: linkTargetSettings?.linkTarget === 'POPUP' ? 'worksheetPopup' : undefined,
        });
      }
      break;
    case 'VIEW':
      {
        const { workbookName, worksheetId, viewId, repositoryName, transferConditionMode } = drillThrough;
        link = await importAndCreateCadenzaLink({
          type: 'view',
          workbookName,
          viewId,
          repositoryName,
          worksheetId,
          transferSelectionAsFilter: true,
          ...commonLinkArguments,
          transferConditionMode,
          ...linkTargetSettings,
          partialContext: linkTargetSettings?.linkTarget === 'POPUP' ? 'viewPopup' : undefined,
        });
      }
      break;
    case 'REPORT':
      {
        const { workbookName, reportId, repositoryName, transferConditionMode } = drillThrough;
        link = await importAndCreateCadenzaLink({
          type: 'report',
          workbookName,
          repositoryName,
          reportId,
          transferSelectionAsFilter: true,
          ...commonLinkArguments,
          transferConditionMode,
          ...linkTargetSettings,
          partialContext: linkTargetSettings?.linkTarget === 'POPUP' ? 'viewPopup' : undefined,
        });
      }
      break;
    case 'EXTERNAL_LINK':
      {
        const { url } = drillThrough;
        link = await importAndCreateCadenzaLink({
          type: 'static',
          url,
          ...linkTargetSettings,
          targetId: id as string,
        });
      }
      break;
  }
  link.execute();
}

async function importAndCreateCadenzaLink(settings: CadenzaLinkSettings) {
  const { CadenzaLink } = await import('cadenza/ui/link/cadenza-link');
  return new CadenzaLink(settings);
}
